<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import CrmAPI from 'dashboard/api/crm';
import AutomationAPI from 'dashboard/api/automation';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import AddAutomationRule from 'dashboard/routes/dashboard/settings/automation/AddAutomationRule.vue';
import EditAutomationRule from 'dashboard/routes/dashboard/settings/automation/EditAutomationRule.vue';

const macroIds = defineModel('macroIds', { type: Array, default: () => [] });
const automationRuleIds = defineModel('automationRuleIds', {
  type: Array,
  default: () => [],
});
const { t } = useI18n();
const options = ref({ macros: [], automations: [] });
const loading = ref(false);
const error = ref('');
const addDialog = ref(null);
const editDialog = ref(null);
const selectedAutomation = ref({});
const groups = computed(() => [
  { key: 'macros', ids: macroIds.value, route: { name: 'macros_new' } },
  {
    key: 'automations',
    ids: automationRuleIds.value,
    route: {
      name: 'automation_list',
      query: { create: 'crm_stage_entered' },
    },
  },
]);
const load = async () => {
  loading.value = true;
  error.value = '';
  try {
    options.value = (await CrmAPI.actionOptions()).data;
  } catch {
    error.value = t('CRM.stageActions.loadError');
  } finally {
    loading.value = false;
  }
};
const update = (key, ids) => {
  if (key === 'macros') macroIds.value = ids;
  else automationRuleIds.value = ids;
};
const available = group =>
  options.value[group.key]
    .filter(item => !group.ids.includes(item.id) && item.active !== false)
    .map(item => ({ value: item.id, label: item.name }));
const label = (key, id) => {
  const item = options.value[key].find(option => option.id === id);
  if (!item) return t('CRM.stageActions.missing', { id });
  return item.active === false
    ? t('CRM.stageActions.inactiveName', { name: item.name })
    : item.name;
};
const editAutomation = async id => {
  try {
    const response = await AutomationAPI.get();
    selectedAutomation.value = response.data.payload.find(
      item => item.id === id
    );
    if (selectedAutomation.value) editDialog.value?.open();
  } catch {
    error.value = t('CRM.stageActions.loadError');
  }
};
const saveAutomation = async (payload, mode) => {
  try {
    if (mode === 'create') {
      const response = await AutomationAPI.create({
        ...payload,
        active: false,
      });
      automationRuleIds.value = [...automationRuleIds.value, response.data.id];
      addDialog.value?.close();
    } else {
      await AutomationAPI.update(payload.id, payload);
      editDialog.value?.close();
    }
    await load();
  } catch {
    error.value = t('CRM.stageActions.saveError');
  }
};
const toggleAutomation = async id => {
  const item = options.value.automations.find(option => option.id === id);
  if (!item) return;
  try {
    await AutomationAPI.update(id, { active: !item.active });
    await load();
  } catch {
    error.value = t('CRM.stageActions.saveError');
  }
};
onMounted(load);
</script>

<template>
  <details class="group rounded-lg border border-n-weak">
    <summary
      class="flex min-h-12 cursor-pointer list-none items-center gap-2 p-3 [&::-webkit-details-marker]:hidden"
    >
      <span
        class="i-lucide-zap size-4 shrink-0 text-n-slate-11"
        aria-hidden="true"
      />
      <span class="flex-1">
        <span class="block font-medium">{{
          $t('CRM.stageActions.title')
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          $t('CRM.stageActions.count', {
            count: macroIds.length + automationRuleIds.length,
          })
        }}</span>
      </span>
      <span
        class="i-lucide-chevron-down size-4 text-n-slate-11 group-open:rotate-180"
        aria-hidden="true"
      />
    </summary>
    <div class="flex flex-col gap-4 px-3 pb-3">
      <p class="m-0 text-xs leading-relaxed text-n-slate-11">
        {{ $t('CRM.stageActions.help') }}
      </p>
      <div v-for="group in groups" :key="group.key" class="flex flex-col gap-2">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <span class="text-sm font-medium">{{
            $t(`CRM.stageActions.${group.key}`)
          }}</span>
          <RouterLink
            v-if="group.key === 'macros'"
            :to="group.route"
            target="_blank"
            rel="noopener"
            class="inline-flex items-center gap-1 text-xs text-n-blue-11"
          >
            {{ $t(`CRM.stageActions.create.${group.key}`) }}
            <span class="i-lucide-external-link size-3" aria-hidden="true" />
          </RouterLink>
          <Button
            v-else
            type="button"
            size="sm"
            variant="outline"
            :label="$t('CRM.stageActions.create.automations')"
            @click="addDialog?.open('crm_stage_entered')"
          />
        </div>
        <ComboBox
          :key="`${group.key}-${group.ids.join('-')}`"
          :options="available(group)"
          :disabled="loading || !!error || group.ids.length >= 10"
          :placeholder="$t(`CRM.stageActions.select.${group.key}`)"
          :aria-label="$t(`CRM.stageActions.select.${group.key}`)"
          :empty-state="$t('CRM.stageActions.empty')"
          :search-placeholder="$t('CRM.search')"
          @update:model-value="
            id => id && update(group.key, [...group.ids, id])
          "
        />
        <ul
          v-if="group.ids.length"
          class="m-0 flex list-none flex-col gap-1 p-0"
        >
          <li
            v-for="id in group.ids"
            :key="id"
            class="flex min-w-0 items-center gap-2 rounded-lg bg-n-solid-3 px-3 py-1"
          >
            <span class="min-w-0 flex-1 break-words text-xs">{{
              label(group.key, id)
            }}</span>
            <template v-if="group.key === 'automations'">
              <Button
                type="button"
                icon="i-lucide-pen"
                size="xs"
                variant="ghost"
                :aria-label="$t('CRM.stageActions.edit')"
                @click="editAutomation(id)"
              />
              <Button
                type="button"
                :icon="
                  options.automations.find(item => item.id === id)?.active
                    ? 'i-lucide-pause'
                    : 'i-lucide-play'
                "
                size="xs"
                variant="ghost"
                :aria-label="$t('CRM.stageActions.toggle')"
                @click="toggleAutomation(id)"
              />
            </template>
            <Button
              type="button"
              icon="i-lucide-x"
              size="xs"
              variant="ghost"
              color="slate"
              :aria-label="
                $t('CRM.stageActions.remove', { name: label(group.key, id) })
              "
              @click="
                update(
                  group.key,
                  group.ids.filter(value => value !== id)
                )
              "
            />
          </li>
        </ul>
      </div>
      <p v-if="error" role="alert" class="m-0 text-xs text-n-ruby-11">
        {{ error }}
      </p>
      <div class="flex items-center gap-2">
        <p class="m-0 flex-1 text-xs text-n-slate-11">
          {{ $t('CRM.stageActions.refreshHelp') }}
        </p>
        <Button
          type="button"
          icon="i-lucide-refresh-cw"
          size="sm"
          variant="outline"
          color="slate"
          :label="$t('CRM.refresh')"
          :disabled="loading"
          @click="load"
        />
      </div>
    </div>
  </details>
  <AddAutomationRule ref="addDialog" @save-automation="saveAutomation" />
  <EditAutomationRule
    ref="editDialog"
    :selected-response="selectedAutomation"
    @save-automation="saveAutomation"
  />
</template>
